import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "utils/api";
import type { UseDeletePlanArgs, UseDeletePlanResult } from "./types";

export const useDeletePlan = ({
  planRecords,
  onPlanDeleted,
}: UseDeletePlanArgs): UseDeletePlanResult => {
  const { t } = useTranslation();

  const [deletingRecordIndex, setDeletingRecordIndex] = useState<number | null>(
    null,
  );
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  const deletingRecord =
    deletingRecordIndex !== null
      ? (planRecords[deletingRecordIndex] ?? null)
      : null;

  const isDeleteConfirmOpen = deletingRecord !== null;

  const openDeletePlan = useCallback(
    (recordIndex: number) => {
      if (!planRecords[recordIndex]) return;
      setDeletingRecordIndex(recordIndex);
    },
    [planRecords],
  );

  const closeDeletePlan = useCallback(() => {
    setDeletingRecordIndex(null);
  }, []);

  const confirmDeletePlan = useCallback(async () => {
    if (deletingRecord === null || deletingRecordIndex === null) return;
    setIsDeleteLoading(true);
    try {
      await api.deleteQuantitySurveyorFloorPlan(deletingRecord.id);
      onPlanDeleted?.(deletingRecordIndex);
      setDeletingRecordIndex(null);
    } catch {
      toast.error(t("smartSurveyor.planActions.deleteFailed"));
    } finally {
      setIsDeleteLoading(false);
    }
  }, [deletingRecord, deletingRecordIndex, onPlanDeleted, t]);

  return {
    isDeleteConfirmOpen,
    isDeleteLoading,
    deletingPlanLabel: deletingRecord?.name ?? "",
    openDeletePlan,
    closeDeletePlan,
    confirmDeletePlan,
  };
};
